import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { Inbox, Users, Briefcase, Sparkles } from "lucide-react";

const Stat = ({ icon: Icon, label, value, to, accent }) => (
  <Link
    to={to}
    className="block bg-[#13121f] border border-white/10 rounded-2xl p-6 hover:border-[#f5c76e]/30 transition-colors"
  >
    <div className="flex items-center justify-between">
      <div className="w-10 h-10 rounded-full border border-[#f5c76e]/30 bg-[#f5c76e]/[0.06] flex items-center justify-center text-[#f5c76e]">
        <Icon size={18} strokeWidth={1.4} />
      </div>
      {accent && <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#f5c76e]">{accent}</span>}
    </div>
    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6b6677] mt-6">{label}</p>
    <p className="font-display text-4xl font-bold mt-1">{value}</p>
  </Link>
);

const Dashboard = () => {
  const [stats, setStats] = useState({ leads: 0, newLeads: 0, clients: 0, projects: 0, active: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [leads, clients, projects] = await Promise.all([
          api.get("/leads"),
          api.get("/clients"),
          api.get("/projects"),
        ]);
        setStats({
          leads: leads.data.length,
          newLeads: leads.data.filter((l) => l.status === "new").length,
          clients: clients.data.length,
          projects: projects.data.length,
          active: projects.data.filter((p) => p.status === "active").length,
        });
        setRecent(leads.data.slice(0, 5));
      } catch (e) { /* ignore */ }
    })();
  }, []);

  return (
    <div data-testid="dashboard-page">
      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#9c95a8]">Welcome back</p>
      <h1 className="font-display text-4xl font-bold tracking-tight mt-2">
        Mission control.
      </h1>
      <p className="text-[#bdb6c4] mt-2 max-w-xl">
        Quick view of what's coming in and what's in flight.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={Inbox} label="Total leads" value={stats.leads} to="/admin/leads" accent={stats.newLeads ? `${stats.newLeads} NEW` : null} />
        <Stat icon={Sparkles} label="New leads" value={stats.newLeads} to="/admin/leads" />
        <Stat icon={Users} label="Clients" value={stats.clients} to="/admin/clients" />
        <Stat icon={Briefcase} label="Active projects" value={stats.active} to="/admin/projects" accent={`${stats.projects} TOTAL`} />
      </div>

      <div className="mt-10">
        <h2 className="font-display text-xl font-bold mb-4">Recent postcards</h2>
        {recent.length === 0 ? (
          <p className="text-[#9c95a8] text-sm">No leads yet — they'll appear here as postcards roll in.</p>
        ) : (
          <div className="space-y-2">
            {recent.map((l) => (
              <Link
                key={l.id}
                to={`/admin/leads/${l.id}`}
                className="flex items-center justify-between bg-[#13121f] border border-white/10 rounded-xl px-4 py-3 hover:border-[#f5c76e]/30 transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{l.name} · <span className="text-[#9c95a8] font-normal">{l.email}</span></p>
                  <p className="text-sm text-[#bdb6c4] truncate">{l.idea}</p>
                </div>
                <span className={`font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded border ${
                  l.status === "new" ? "border-[#f5c76e]/40 text-[#f5c76e]" :
                  l.status === "converted" ? "border-emerald-400/40 text-emerald-400" :
                  "border-white/15 text-[#9c95a8]"
                }`}>{l.status}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
